import { useActive, useCommands } from '@remirror/react';
import { CommandButton } from '../CommandButton';
import { DownOutlined } from '@ant-design/icons';
import React, { useCallback, useMemo } from 'react';
import {
  CodeBlockIcon,
  CodeIcon,
  Heading1Icon,
  Heading2Icon,
  Heading3Icon,
  NumberListIcon,
  BulletListIcon,
  QuoteIcon,
  TextIcon,
  TodoListIcon,
  TickOutlined,
} from './../../icons';
import { Dropdown } from './../Dropdown';
import { Flex, Typography } from 'antd';
import './NodeConverterButton.css';

const { Text } = Typography;

const ICON_MAP = {
  text_icon: { component: <TextIcon /> },
  heading_1_icon: { component: <Heading1Icon /> },
  heading_2_icon: { component: <Heading2Icon /> },
  heading_3_icon: { component: <Heading3Icon /> },
  code_icon: { component: <CodeIcon /> },
  bullet_list_icon: { component: <BulletListIcon /> },
  numbered_list_icon: { component: <NumberListIcon /> },
  to_do_list_icon: { component: <TodoListIcon /> },
  quote_icon: { component: <QuoteIcon /> },
  code_snippet_icon: { component: <CodeBlockIcon /> },
};

export const getActiveNodeType = (active) => {
  let activeNodeType = 'paragraph'; // Default to paragraph
  if (active.codeBlock()) {
    activeNodeType = 'code_snippet';
  } else if (active.heading({ level: 1 })) {
    activeNodeType = 'heading_1';
  } else if (active.heading({ level: 2 })) {
    activeNodeType = 'heading_2';
  } else if (active.heading({ level: 3 })) {
    activeNodeType = 'heading_3';
  } else if (active.bulletList()) {
    activeNodeType = 'bullet_list';
  } else if (active.orderedList()) {
    activeNodeType = 'numbered_list';
  } else if (active.taskList()) {
    activeNodeType = 'to_do_list';
  } else if (active.code()) {
    activeNodeType = 'code';
  } else if (active.codeBlock()) {
    activeNodeType = 'code_snippet';
  } else if (active.blockquote()) {
    activeNodeType = 'quote';
  }
  return activeNodeType;
};

export const getActiveNodeDisplayName = (active, nodes) => {
  const node = nodes?.find((node) => node?.key === active);
  return node?.display;
};
export const NodeRenderer = React.memo(({ getIcon, onClick, nodes, activeNode }) => {
  return (
    <>
      {nodes.map(({ icon, display, key, callback }) => {
        const isEnabled = typeof callback === 'boolean' ? callback : callback?.enabled();

        return (
          <Flex
            onClick={() => {
              if (isEnabled) {
                onClick?.({ key, isEnabled });
              }
            }}
            key={key}
            className={`dropdown__node-item${!isEnabled ? ' disabled' : ''}`}
          >
            {icon && <Flex className="dropdown__node-icon">{getIcon(icon)}</Flex>}
            <Flex className="dropdown__node-text">
              <Text className="text">{display}</Text>
            </Flex>
            {activeNode && activeNode === key && <TickOutlined />}
          </Flex>
        );
      })}
    </>
  );
});

export const NodeConverterButton = () => {
  const active = useActive();
  const {
    toggleCode,
    toggleCodeBlock,
    toggleHeading,
    toggleBulletList,
    toggleOrderedList,
    toggleTaskList,
    toggleBlockquote,
    focus,
  } = useCommands();

  const getIcon = useCallback((iconType) => {
    return ICON_MAP[iconType]?.component;
  }, []);

  const nodes = useMemo(() => {
    return [
      { key: 'paragraph', display: 'Text', icon: 'text_icon', callback: true },
      { key: 'heading_1', display: 'Heading 1', icon: 'heading_1_icon', callback: toggleHeading },
      { key: 'heading_2', display: 'Heading 2', icon: 'heading_2_icon', callback: toggleHeading },
      { key: 'heading_3', display: 'Heading 3', icon: 'heading_3_icon', callback: toggleHeading },
      { key: 'bullet_list', display: 'Bullet list', icon: 'bullet_list_icon', callback: toggleBulletList },
      { key: 'numbered_list', display: 'Numbered list', icon: 'numbered_list_icon', callback: toggleOrderedList },
      { key: 'to_do_list', display: 'To-do list', icon: 'to_do_list_icon', callback: toggleTaskList },
      { key: 'code', display: 'Code', icon: 'code_icon', callback: toggleCode },
      { key: 'code_snippet', display: 'Code snippet', icon: 'code_snippet_icon', callback: toggleCodeBlock },
      { key: 'quote', display: 'Quote', icon: 'quote_icon', callback: toggleBlockquote },
    ];
  }, [
    toggleHeading,
    toggleBlockquote,
    toggleBulletList,
    toggleOrderedList,
    toggleTaskList,
    toggleCode,
    toggleCodeBlock,
  ]);

  const onClickHandler = ({ key, isEnabled }) => {
    switch (key) {
      case 'paragraph':
        let enabled = false;
        let activeLevel = 1;
        for (let i = 1; i <= 6; i++)
          if (active.heading({ level: i })) {
            enabled = true;
            activeLevel = i;
          }
        if (enabled) toggleHeading({ level: activeLevel });
        return;

      case 'heading_1':
      case 'heading_2':
      case 'heading_3':
      case 'heading_4':
      case 'heading_5':
      case 'heading_6':
        const keys = key.split('_');
        const level = parseInt(keys[1]);
        if (isEnabled) toggleHeading({ level });
        break;

      case 'code':
        if (isEnabled) toggleCode();
        break;

      case 'code_snippet':
        if (isEnabled) toggleCodeBlock();
        break;

      case 'bullet_list':
        if (isEnabled) toggleBulletList();
        break;

      case 'numbered_list':
        if (isEnabled) toggleOrderedList();
        break;

      case 'to_do_list':
        if (isEnabled) toggleTaskList();
        break;

      case 'quote':
        if (isEnabled) toggleBlockquote();
        break;
    }
    focus();
  };

  const activeNode = getActiveNodeType(active);

  return (
    <Dropdown
      overlayClassName="toolbar__button-dropdown node-converter-dropdown"
      className="toolbar__button"
      dropdownRender={() => (
        <NodeRenderer activeNode={activeNode} nodes={nodes} getIcon={getIcon} onClick={onClickHandler} />
      )}
      placement="bottomLeft"
    >
      <CommandButton>
        <Text className="command__button-text">{getActiveNodeDisplayName(activeNode, nodes)}</Text>
        <DownOutlined className="sm" />
      </CommandButton>
    </Dropdown>
  );
};

export default NodeConverterButton;
